import { RoleViewEnum } from '@/interfaces/profile.interface';
import dynamic from 'next/dynamic';
import { useContext, useEffect, useState } from 'react';
import AuthContext from 'state/auth/AuthContext';
import {
  EHomepageTabSectionKey,
  IHomepageTabCustomSection,
  IHomepageTabEventSection,
  IHomepageTabFeaturedTopicsSection,
  IHomepageTabFooterSection,
  IHomepageTabHighlightVideosSection,
  IHomepageTabIntroductionSection,
  IHomepageTabLiveStreamSection,
  IHomepageTabSuggestedCoursesSection,
  TEnterpriseProfileHomepageTabSection,
  TEnterpriseProfileHomepageTabSectionElements,
} from '../../../../../../../interfaces/enterprise-profile/homepage_tab.interface';
import { EnterpriseProfileDataContext } from '../../context/EnterpriseProfileDataContext';
import EnterprofileTabContext from '../../context/EnterpriseProfileTabContext';

const HomepageEventsSection = dynamic(() => import('./HomepageEventsSection'));
const HomepageFeaturedTopicsSection = dynamic(
  () => import('./HomepageFeaturedTopicsSection')
);
const HomepageFooterSection = dynamic(() => import('./HomepageFooterSection'));
const HomepageHighlightCoursesSection = dynamic(
  () => import('./HomepageHighlightCoursesSection')
);
const HomepageHighlightVideosSection = dynamic(
  () => import('./HomepageHighlightVideosSection')
);
const HomepageIntroductionSection = dynamic(
  () => import('./HomepageIntroductionSection')
);
const HomepageMaybeYouKnowSection = dynamic(
  () => import('./HomepageMaybeYouKnowSection')
);
const HomepageSuggestedCoursesSection = dynamic(
  () => import('./HomepageSuggestedCoursesSection')
);
const HomepageCustomSection = dynamic(() => import('./HomepageCustomSection'));
const HomepageLiveStreamSection = dynamic(
  () => import('./HomepageLiveStreamSection')
);

const hashComponentDueToKey = (
  key: EHomepageTabSectionKey,
  sectionName: string,
  props: TEnterpriseProfileHomepageTabSectionElements | undefined
) => {
  const hashSection = {
    [EHomepageTabSectionKey.Introduction]: (
      <HomepageIntroductionSection
        sectionName={sectionName}
        {...(props as IHomepageTabIntroductionSection)}
      />
    ),
    [EHomepageTabSectionKey.Events]: (
      <HomepageEventsSection
        {...(props as IHomepageTabEventSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.MaybeYouKnow]: (
      <HomepageMaybeYouKnowSection sectionName={sectionName} />
    ),
    [EHomepageTabSectionKey.SuggestedCourses]: (
      <HomepageSuggestedCoursesSection
        {...(props as IHomepageTabSuggestedCoursesSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.HighlightCourses]: (
      <HomepageHighlightCoursesSection sectionName={sectionName} />
    ),
    [EHomepageTabSectionKey.HighlightVideos]: (
      <HomepageHighlightVideosSection
        {...(props as IHomepageTabHighlightVideosSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Footer]: (
      <HomepageFooterSection
        {...(props as IHomepageTabFooterSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.FeaturedTopics]: (
      <HomepageFeaturedTopicsSection
        {...(props as IHomepageTabFeaturedTopicsSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.NavigateButton]: null,
    [EHomepageTabSectionKey.Custom1]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom2]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom3]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom4]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom5]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom6]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom7]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom8]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom9]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.Custom10]: (
      <HomepageCustomSection
        {...(props as IHomepageTabCustomSection)}
        sectionName={sectionName}
      />
    ),
    [EHomepageTabSectionKey.LiveStream]: (
      <HomepageLiveStreamSection
        {...(props as IHomepageTabLiveStreamSection)}
        sectionName={sectionName}
      />
    ),
  };

  return hashSection[key];
};

const EnterpriseHomepageTab = () => {
  const data = useContext(
    EnterprofileTabContext
  ) as TEnterpriseProfileHomepageTabSection;
  const { initData: profileData } = useContext(EnterpriseProfileDataContext);

  const { isLoggedIn } = useContext(AuthContext);

  const [disabledMaybeYouKnowSection, setDisabledMaybeYouKnowSection] =
    useState(false);

  useEffect(() => {
    setDisabledMaybeYouKnowSection(
      !isLoggedIn() || profileData?.viewPermission === RoleViewEnum.Owner
    );
  }, [isLoggedIn]);

  return (
    <div className="flex flex-col gap-0 lg:gap-10">
      {data
        .filter((item) =>
          item.sectionKey === EHomepageTabSectionKey.MaybeYouKnow
            ? item.isActive && !disabledMaybeYouKnowSection
            : item.isActive
        )
        .map((item) => (
          <div id={item.sectionKey} key={item.sectionKey} className="w-full">
            {hashComponentDueToKey(
              item.sectionKey,
              item.sectionName,
              item.elements
            )}
          </div>
        ))}
    </div>
  );
};
export default EnterpriseHomepageTab;
